import { twMerge } from 'tailwind-merge';

type Props = React.HTMLAttributes<HTMLSpanElement> & {
  children: React.ReactNode;
  variant?:
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6'
    | 'title'
    | 'subtitle'
    | 'details'
    | 'body'
    | 'body-md'
    | 'body-lg'
    | 'label-md'
    | 'label-lg'
    | 'label-xl'
    | 'link-md'
    | 'link-lg'
    | 'link-xl'
    | 'caption-md'
    | 'caption-lg'
    | 'caption-link-lg'
    | 'button-sm'
    | 'button-md'
    | 'button-lg'
    | 'button-xl'
    | 'dt'
    | 'dd'
    | 'gray900-weight-normal'
    | 'gray600-size-sm'
    | 'gray600'
    | 'navy700'
    | 'navy800'
    | 'red500-size-sm'
    | 'button-underline';
  classes?: string[];
};

interface Map {
  key: string;
  class: string[];
}

const VariantSetting: Map[] = [
  { key: 'h1', class: ['text-gray-900', 'text-[42px]', 'font-bold', 'leading-[140%]', 'tracking-[0.04em]'] },
  { key: 'h2', class: ['text-gray-900', 'text-[32px]', 'font-bold', 'leading-[150%]', 'tracking-[0.04em]'] },
  { key: 'h3', class: ['text-gray-900', 'text-[26px]', 'font-bold', 'leading-[150%]', 'tracking-[0.04em]'] },
  { key: 'h4', class: ['text-gray-900', 'text-[22px]', 'font-bold', 'leading-[150%]', 'tracking-[0.04em]'] },
  { key: 'h5', class: ['text-gray-900', 'text-[20px]', 'font-bold', 'leading-[150%]', 'tracking-[0.04em]'] },
  { key: 'h6', class: ['text-gray-900', 'text-[18px]', 'font-bold', 'leading-[150%]', 'tracking-[0.04em]'] },
  { key: 'title', class: ['text-lg'] },
  { key: 'subtitle', class: ['prose', 'font-semibold'] },
  { key: 'body', class: ['prose'] },
  { key: 'body-md', class: ['text-gray-600', 'text-sm', 'font-normal', 'leading-[170%]', 'tracking-[0.04em]'] },
  { key: 'body-lg', class: ['text-gray-900', 'text-base', 'font-normal', 'leading-[170%]', 'tracking-[0.04em]'] },
  { key: 'label-md', class: ['text-gray-900', 'text-[14px]', 'font-medium', 'leading-[150%]', 'tracking-[0.04em]'] },
  { key: 'label-lg', class: ['text-gray-900', 'text-[16px]', 'font-medium', 'leading-[150%]', 'tracking-[0.04em]'] },
  { key: 'label-xl', class: ['text-gray-900', 'text-[20px]', 'font-medium', 'leading-[150%]', 'tracking-[0.04em]'] },
  {
    key: 'link-md',
    class: ['text-gray-900', 'text-[14px]', 'font-medium', 'leading-[150%]', 'tracking-[0.04em]', 'underline'],
  },
  {
    key: 'link-lg',
    class: ['text-gray-900', 'text-[16px]', 'font-medium', 'leading-[150%]', 'tracking-[0.04em]', 'underline'],
  },
  {
    key: 'link-xl',
    class: ['text-gray-900', 'text-[20px]', 'font-medium', 'leading-[150%]', 'tracking-[0.04em]', 'underline'],
  },
  {
    key: 'caption-md',
    class: ['text-gray-400', 'text-[10px]', 'font-normal', 'leading-[170%]', 'tracking-[0.02em]'],
  },
  { key: 'caption-lg', class: ['text-gray-400', 'text-xs', 'font-normal', 'leading-[170%]', 'tracking-[0.02em]'] },
  {
    key: 'caption-link-lg',
    class: ['text-Navy-800', 'text-[12px]', 'font-normal', 'leading-[170%]', 'tracking-[0.02em]', 'underline'],
  },
  { key: 'button-sm', class: ['text-[12px]', 'font-normal', 'leading-[150%]', 'tracking-[0.04em]'] },
  { key: 'button-md', class: ['text-[14px]', 'font-normal', 'leading-[150%]', 'tracking-[0.04em]'] },
  { key: 'button-lg', class: ['text-[16px]', 'font-normal', 'leading-[150%]', 'tracking-[0.04em]'] },
  { key: 'button-xl', class: ['text-[20px]', 'font-normal', 'leading-[150%]', 'tracking-[0.04em]'] },
  { key: 'details', class: ['mt-1', 'max-w-2xl', 'text-sm', 'text-gray-500'] },
  { key: 'dt', class: ['flex', 'text-sm', 'font-medium', 'text-gray-500'] },
  { key: 'dd', class: ['mt-1', 'text-sm', 'text-gray-900', 'sm:mt-0', 'sm:col-span-2'] },
  // TODO: 整理したい↓。カラー指定しかしていなかったり、わざわざこのコンポーネントを使わなくていい気がする。（使い所おさえて適切なkey名に変更する...？）
  { key: 'gray900-weight-normal', class: ['text-gray-900', 'font-medium'] },
  { key: 'gray600-size-sm', class: ['text-gray-600', 'text-sm'] },
  { key: 'gray600', class: ['text-gray-600'] },
  { key: 'navy700', class: ['text-[#1A1B71]'] },
  { key: 'navy800', class: ['text-Navy-800'] },
  { key: 'red500-size-sm', class: ['text-sm', 'text-red-500'] },
  { key: 'button-underline', class: ['text-gray-500', 'font-normal', '[&>*]:underline', '[&>*]:underline-offset-2'] },
];

const Typography = ({ children, variant = 'body', classes = [], ...props }: Props): JSX.Element => {
  const { class: _classes } = VariantSetting.find((map: Map) => map.key === variant);
  const className = twMerge([..._classes, ...classes].join(' '));

  return (
    <span className={className} {...props}>
      {children}
    </span>
  );
};

export default Typography;
